import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  CuotaDeNodo,
  EntradaPermiso,
  Grupo,
  ListadoCarpeta,
  Nodo,
  NodoArbol,
  PermisosDeNodo,
  ResultadoBusqueda,
  VersionArchivo,
} from '@doc/shared';
import { api } from '../../lib/api.js';

export const claves = {
  arbol: ['arbol'] as const,
  carpeta: (id: string) => ['carpeta', id] as const,
  busqueda: (q: string, raizId?: string) => ['busqueda', q, raizId ?? ''] as const,
  versiones: (id: string) => ['versiones', id] as const,
  permisos: (id: string) => ['permisos', id] as const,
  sujetos: ['sujetos'] as const,
  cuota: (id: string) => ['cuota', id] as const,
  papelera: ['papelera'] as const,
};

/**
 * Cualquier cambio en el árbol invalida las tres vistas que lo miran: el
 * panel lateral, el contenido de la carpeta y la papelera. Centralizarlo evita
 * el clásico "moví un archivo y la lista de al lado quedó mintiendo".
 */
export function refrescarTodo(cliente: QueryClient): void {
  void cliente.invalidateQueries({ queryKey: claves.arbol });
  void cliente.invalidateQueries({ queryKey: ['carpeta'] });
  void cliente.invalidateQueries({ queryKey: ['busqueda'] });
  void cliente.invalidateQueries({ queryKey: claves.papelera });
  // Subir, borrar o purgar cambia el espacio ocupado: si no se invalidan, la
  // barra de cuota sigue mostrando la foto anterior.
  void cliente.invalidateQueries({ queryKey: ['cuota'] });
  void cliente.invalidateQueries({ queryKey: ['cuotas'] });
}

export const useArbol = () =>
  useQuery({ queryKey: claves.arbol, queryFn: () => api<NodoArbol[]>('/nodos/arbol') });

export const useCarpeta = (id: string | null) =>
  useQuery({
    queryKey: claves.carpeta(id ?? ''),
    queryFn: () => api<ListadoCarpeta>(`/nodos/${id}/hijos`),
    enabled: !!id,
  });

export const useBusqueda = (q: string, raizId?: string) =>
  useQuery({
    queryKey: claves.busqueda(q, raizId),
    queryFn: () =>
      api<ResultadoBusqueda[]>(`/busqueda?q=${encodeURIComponent(q)}${raizId ? `&raizId=${raizId}` : ''}`),
    enabled: q.trim().length >= 2,
  });

export const useVersiones = (id: string | null) =>
  useQuery({
    queryKey: claves.versiones(id ?? ''),
    queryFn: () => api<VersionArchivo[]>(`/nodos/${id}/versiones`),
    enabled: !!id,
  });

export const usePermisosDeNodo = (id: string | null) =>
  useQuery({
    queryKey: claves.permisos(id ?? ''),
    queryFn: () => api<PermisosDeNodo>(`/nodos/${id}/permisos`),
    enabled: !!id,
  });

/** Estado de cuota de una carpeta: la propia y la que realmente la limita. */
export const useCuotaDeNodo = (id: string | null, activo = true) =>
  useQuery({
    queryKey: claves.cuota(id ?? ''),
    queryFn: () => api<CuotaDeNodo>(`/nodos/${id}/cuota`),
    enabled: !!id && activo,
  });

export const useSujetos = (activo: boolean) =>
  useQuery({
    queryKey: claves.sujetos,
    queryFn: () =>
      api<{ usuarios: Array<{ id: string; nombre: string; email: string }>; grupos: Grupo[] }>('/sujetos'),
    enabled: activo,
    staleTime: 5 * 60_000,
  });

export function useAcciones() {
  const cliente = useQueryClient();
  const invalidar = () => refrescarTodo(cliente);

  return {
    crearCarpeta: useMutation({
      mutationFn: (v: { padreId: string; nombre: string }) =>
        api<Nodo>('/nodos/carpeta', { metodo: 'POST', cuerpo: v }),
      onSuccess: invalidar,
    }),
    renombrar: useMutation({
      mutationFn: (v: { id: string; nombre: string }) =>
        api<Nodo>(`/nodos/${v.id}`, { metodo: 'PATCH', cuerpo: { nombre: v.nombre } }),
      onSuccess: invalidar,
    }),
    mover: useMutation({
      mutationFn: (v: { ids: string[]; destinoId: string }) =>
        api<{ movidos: string[] }>('/nodos/mover', { metodo: 'POST', cuerpo: v }),
      onSuccess: invalidar,
    }),
    copiar: useMutation({
      mutationFn: (v: { ids: string[]; destinoId: string }) =>
        api<{ copiados: string[] }>('/nodos/copiar', { metodo: 'POST', cuerpo: v }),
      onSuccess: invalidar,
    }),
    eliminar: useMutation({
      mutationFn: (ids: string[]) => api<{ eliminados: string[] }>('/nodos/eliminar', { metodo: 'POST', cuerpo: { ids } }),
      onSuccess: invalidar,
    }),
    restaurar: useMutation({
      mutationFn: (ids: string[]) => api('/papelera/restaurar', { metodo: 'POST', cuerpo: { ids } }),
      onSuccess: invalidar,
    }),
    purgar: useMutation({
      mutationFn: (ids: string[]) => api('/papelera/purgar', { metodo: 'POST', cuerpo: { ids } }),
      onSuccess: invalidar,
    }),
    guardarPermisos: useMutation({
      mutationFn: (v: { id: string; entradas: Array<Pick<EntradaPermiso, 'sujetoTipo' | 'sujetoId' | 'permisos'>> }) =>
        api(`/nodos/${v.id}/permisos`, { metodo: 'PUT', cuerpo: { entradas: v.entradas } }),
      onSuccess: (_datos, v) => {
        void cliente.invalidateQueries({ queryKey: claves.permisos(v.id) });
        invalidar();
      },
    }),
    cambiarHerencia: useMutation({
      mutationFn: (v: { id: string; heredar: boolean; copiarActuales: boolean }) =>
        api(`/nodos/${v.id}/herencia`, {
          metodo: 'POST',
          cuerpo: { heredar: v.heredar, copiarActuales: v.copiarActuales },
        }),
      onSuccess: (_datos, v) => {
        void cliente.invalidateQueries({ queryKey: claves.permisos(v.id) });
        invalidar();
      },
    }),
    restaurarVersion: useMutation({
      mutationFn: (v: { id: string; version: number }) =>
        api<Nodo>(`/nodos/${v.id}/versiones/${v.version}/restaurar`, { metodo: 'POST' }),
      onSuccess: (_datos, v) => {
        void cliente.invalidateQueries({ queryKey: claves.versiones(v.id) });
        invalidar();
      },
    }),
  };
}
